import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const PermissionControlsModal = ({ onClose }) => {
  const { organizationId } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const roles = [
    { value: 'admin', label: 'Admin' },
    { value: 'manager', label: 'Manager' },
    { value: 'salesperson', label: 'Salesperson' },
    { value: 'technician', label: 'Technician' }
  ];

  const permissionsList = [
    { value: 'can_view_dashboard', label: 'View Dashboard', description: 'Access main org dashboard' },
    { value: 'can_view_my_dashboard', label: 'View My Dashboard', description: 'Access personal dashboard' },
    { value: 'can_view_operations', label: 'View Operations', description: 'Access tickets, assignments' },
    { value: 'can_view_sales', label: 'View Sales', description: 'Access quotes, invoices, customers' },
    { value: 'can_view_finance', label: 'View Finance', description: 'Access billing and payments' },
    { value: 'can_view_reports', label: 'View Reports', description: 'Access analytics and reporting' },
    { value: 'can_view_settings', label: 'View Settings', description: 'Access org settings' },
    { value: 'can_create_tickets', label: 'Create Tickets', description: 'Open new service tickets' },
    { value: 'can_edit_tickets', label: 'Edit Tickets', description: 'Modify existing tickets' },
    { value: 'can_delete_tickets', label: 'Delete Tickets', description: 'Remove tickets permanently' },
    { value: 'can_create_quotes', label: 'Create Quotes', description: 'Draft new price quotes' },
    { value: 'can_approve_quotes', label: 'Approve Quotes', description: 'Authorize quote approval' },
    { value: 'can_create_invoices', label: 'Create Invoices', description: 'Generate invoices' },
    { value: 'can_manage_personnel', label: 'Manage Personnel', description: 'Add, edit, remove staff' },
    { value: 'can_manage_customers', label: 'Manage Customers', description: 'Customer database access' },
    { value: 'can_manage_inventory', label: 'Manage Inventory', description: 'Inventory tracking & updates' },
    { value: 'can_manage_billing', label: 'Manage Billing', description: 'Access billing & subscriptions' },
  ];

  useEffect(() => {
    if (organizationId) fetchPermissions();
  }, [organizationId]);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        ?.from('role_permissions')
        ?.select('*')
        ?.eq('organization_id', organizationId)
        ?.order('role', { ascending: true });

      if (fetchError) throw fetchError;

      // Build a map: { role: { can_*: bool, ... } }
      const permMap = {};
      data?.forEach(p => { permMap[p.role] = p; });
      setPermissions(permMap);
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError(err?.message || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const isPermissionGranted = (role, permKey) => {
    return permissions?.[role]?.[permKey] || false;
  };

  const handlePermissionToggle = async (role, permKey, currentValue) => {
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      const newValue = !currentValue;

      // Optimistic update
      setPermissions(prev => ({
        ...prev,
        [role]: {
          ...prev?.[role],
          [permKey]: newValue,
        },
      }));

      const { error: upsertError } = await supabase
        ?.from('role_permissions')
        ?.upsert(
          {
            organization_id: organizationId,
            role: role,
            [permKey]: newValue,
            updated_at: new Date()?.toISOString(),
          },
          { onConflict: 'organization_id,role' }
        );

      if (upsertError) throw upsertError;

      setSuccessMessage('Permission updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating permission:', err);
      setError(err?.message || 'Failed to update permission');
      // Revert optimistic update on error
      await fetchPermissions();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[1400] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
              Permission Controls
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Manage role-based access permissions
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={24} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="p-4 md:p-6">
          {error && (
            <div className="mb-4 p-4 bg-destructive/10 border border-destructive rounded-lg flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-4 bg-primary/10 border border-primary rounded-lg flex items-start gap-3">
              <Icon name="CheckCircle" size={20} color="var(--color-primary)" />
              <p className="text-sm text-primary">{successMessage}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3 text-sm font-semibold text-foreground">
                      Permission
                    </th>
                    {roles?.map((role) => (
                      <th key={role?.value} className="text-center p-3 text-sm font-semibold text-foreground">
                        {role?.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {permissionsList?.map((permission) => (
                    <tr key={permission?.value} className="border-b border-border hover:bg-muted/50 transition-smooth">
                      <td className="p-3">
                        <div>
                          <p className="text-sm font-medium text-foreground">{permission?.label}</p>
                          <p className="text-xs text-muted-foreground">{permission?.description}</p>
                        </div>
                      </td>
                      {roles?.map((role) => {
                        const isGranted = isPermissionGranted(role?.value, permission?.value);
                        return (
                          <td key={role?.value} className="p-3 text-center">
                            <div className="flex justify-center">
                              <Checkbox
                                checked={isGranted}
                                onChange={() => handlePermissionToggle(role?.value, permission?.value, isGranted)}
                                disabled={saving}
                              />
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex gap-3 pt-6 border-t border-border mt-6">
            <Button variant="default" onClick={onClose} fullWidth>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionControlsModal;